// client/src/pages/Home.js

import {
    Box, Flex, IconButton, Tooltip, useDisclosure,
    AlertDialog, AlertDialogBody, AlertDialogFooter,
    AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, Button,
    Spinner, Text,
} from "@chakra-ui/react";
import { HamburgerIcon, AddIcon, CloseIcon } from "@chakra-ui/icons";  // Add CloseIcon import
import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import OnboardingDialog from '../components/OnboardingDialog';
import { useAuth } from '../context/AuthContext';
import { useCreditUsageContext } from '../context/CreditContext';
import { pruneOldSessions } from "../store/chatSessionStore";

// Components
import Navbar from "../components/Navbar";
import ChatInput from "../components/ChatInputContainer";
import CreditUsageAlert from "../components/CreditUsageAlert";
import HeroSection from "../components/HeroSection";
import Sidebar from "../components/Sidebar";
import LeftPane from "../pages/LeftPane";
import RightPane from "../pages/RightPane";

// Hooks & Utils
import { useChat } from "../hooks/useChat";
import { createResizeHandler } from "../utils/chatHelpers";

export default function Home() {

    const { user, checkAuth } = useAuth();
    const credit = useCreditUsageContext();
    const location = useLocation();
    const navigate = useNavigate();
    const { chatId } = useParams();
    const [showOnboarding, setShowOnboarding] = useState(false);

    const {
        isOpen: isNewChatConfirmOpen,
        onOpen: onNewChatConfirmOpen,
        onClose: onNewChatConfirmClose
    } = useDisclosure();
    const cancelNewChatRef = useRef();

    // Detect ?onboarding=true on page load (signup redirect)
    useEffect(() => {
        const search = location.search || window.location.search;
        const params = new URLSearchParams(search);
        if (params.get('onboarding') === 'true') {
            setShowOnboarding(true);
        }
    }, [location.search]);

    // Enforce onboarding if user's profile is incomplete
    useEffect(() => {
        if (user && user.onboardingCompleted === false) {
            setShowOnboarding(true);
        }
    }, [user]);

    useEffect(() => {
        pruneOldSessions(chatId ? [chatId] : []);
    }, [chatId]);

    const handleOnboardingComplete = async () => {
        await checkAuth(); // refresh user in AuthContext with new profile data
        setShowOnboarding(false);
        navigate('/', { replace: true }); // remove ?onboarding=true from URL
    };

    // UI State — persist sidebar across navigations
    const [isSidebarOpen, setSidebarOpen] = useState(() => {
        return sessionStorage.getItem('sidebarOpen') === 'true';
    });

    // Incremented when a new project is created — triggers sidebar to re-fetch project list
    const [sidebarRefreshKey, setSidebarRefreshKey] = useState(0);

    // Sync sidebar state to sessionStorage so it survives page navigation
    useEffect(() => {
        sessionStorage.setItem('sidebarOpen', isSidebarOpen);
    }, [isSidebarOpen]);

    // Resizable pane state
    const DEFAULT_LEFT_WIDTH = 600;
    const MIN_LEFT_WIDTH = 420;
    const MAX_LEFT_WIDTH = 900;
    const [leftWidth, setLeftWidth] = useState(DEFAULT_LEFT_WIDTH);

    // Chat logic from custom hook
    const {
        chatStarted,
        isSending,
        turns,
        activeSuggestionTurnId,
        suggestions,
        typingTurnId,
        typingSuggestionId,
        activeTurn,
        isRightPaneOpen,
        messagesContainerRef,
        rightPaneRef,
        handleSend,
        toggleSuggestion,
        toggleReport,
        onScrollMessages,
        handleRightPaneScroll,
        closeSuggestionPane,
        extractedSuggestions,
        selectedSuggestions,
        confirmSuggestionSelection,
        handleGenerateReport,
        getRightPaneContent,
        reports,
        activeReportId,
        typingReportId,
        isLoadingSuggestions,
        isLoadingReport,
        saveFeedback,
        reportLoadingPhase,
        projectId,
        hasMoreTurns,
        isLoadingOlderTurns,
        resetChat,
        loadOlderTurns,
        feedbackState,
        setFeedbackState,
        onEnsureExtractedSuggestions,
        suggestionExtractionStatus,
        isChatHydrating,
    } = useChat(chatId);

    const isChatRoute = Boolean(chatId);
    const isChatLoading = isChatRoute && isChatHydrating;

    const handleNewChat = () => {
        if (isSending) {
            // streaming in progress — warn user
            onNewChatConfirmOpen();
        } else {
            // safe — reset state and navigate to /
            resetChat();
            navigate('/');
        }
    };

    // Auto-navigate when a project is first created (first message sent from /)
    useEffect(() => {
        if (projectId && !chatId) {
            navigate(`/chat/${projectId}`, { replace: true });
            setSidebarRefreshKey(prev => prev + 1); // trigger sidebar to re-fetch project list
        }
    }, [projectId, chatId, navigate]);

    // Resize handler
    const handleSeparatorMouseDown = createResizeHandler(
        leftWidth,
        setLeftWidth,
        MIN_LEFT_WIDTH,
        MAX_LEFT_WIDTH
    );

    const rightPaneContent = getRightPaneContent();
    const isTypingInRightPane =
        (typingSuggestionId === activeSuggestionTurnId && rightPaneContent?.type === 'suggestions') ||
        (typingReportId?.turnId === activeSuggestionTurnId && typingReportId?.version === activeReportId?.version);

    return (
        <Box>
            <Navbar />
            <Box
                ml={2}
            // minHeight="100vh"
            >
                {/* Sidebar toggle + New Chat — vertical stack, moves with sidebar */}
                <Flex
                    position="fixed"
                    mt={4}
                    ml={1}
                    left={isSidebarOpen ? "270px" : "20px"}
                    transition="left 0.25s ease"
                    zIndex={1200}
                    flexDirection="column"
                    gap="8px"
                >
                    {/* Hamburger — always visible in both views */}
                    <IconButton
                        icon={isSidebarOpen ? <CloseIcon boxSize={3} color="#CF008A" /> : <HamburgerIcon />}  // Toggle icon based on state
                        aria-label={isSidebarOpen ? "Close Sidebar" : "Toggle Sidebar"}  // Update aria-label
                        bg="white"
                        border={isSidebarOpen ? "2px solid #CF008A" : "1px solid #ECECEC"}
                        color="#242424"
                        borderRadius="12px"
                        _hover={{ bg: "#fff", color: "#CF008A", border: "2px solid #CF008A" }}
                        onClick={() => setSidebarOpen(prev => !prev)}
                    />

                    {/* New Chat — only in split view, fades when sidebar open */}
                    {chatStarted && (
                        <Tooltip
                            label="New Chat"
                            placement="right"
                            bg="#CF008A"
                            color="white"
                            fontSize="sm"
                            fontWeight="medium"
                            px={3}
                            py={2}
                            borderRadius="8px"
                            hasArrow
                            arrowSize={8}
                        >
                            <IconButton
                                icon={<AddIcon />}
                                aria-label="New Chat"
                                bg="white"
                                border="2px solid #CF008A"
                                color="#CF008A"
                                borderRadius="12px"
                                opacity={isSidebarOpen ? 0 : 1}
                                pointerEvents={isSidebarOpen ? 'none' : 'auto'}
                                transition="opacity 0.2s ease"
                                _hover={{ bg: "#CF008A", color: "white" }}
                                onClick={handleNewChat}
                            />
                        </Tooltip>
                    )}
                </Flex>

                <Sidebar
                    isOpen={isSidebarOpen}
                    activeProjectId={projectId}
                    refreshKey={sidebarRefreshKey}
                    onNewProject={() => { resetChat(); navigate('/'); }}
                    onSelectProject={(id, name) => {
                        navigate(`/chat/${id}`);
                    }}
                />

                {/* Main Content Area */}
                <>
                    {!chatId && !chatStarted ? (
                        // HOME VIEW
                        <Box
                            minHeight='100vh'
                            boxSizing='border-box'
                            transition="margin-left 0.25s ease"
                            marginLeft={isSidebarOpen ? "250px" : "0px"}

                            border="1px solid #ECECEC"
                            borderRadius="20px"
                            mt={75}
                            mr={2}
                            mb={2}
                            bgGradient="linear(to-b, #EAFFFF 0%, white 30%, white 60%, #EAFFFF 100%)"
                        >
                            <Flex justify="center" px={4}
                            >
                                <Box maxW="clamp(800px, 50vw, 2000px)" width="100%">
                                    <HeroSection />
                                    <CreditUsageAlert />
                                    <ChatInput key={chatId || 'draft'} chatId={chatId} onSend={handleSend} mode="hero" isLoading={isSending} disabled={credit.isCreditUnavailable} />
                                </Box>
                            </Flex>
                        </Box>
                    ) : (
                        // SPLIT VIEW (Chat Mode)
                        <Box
                            maxHeight='100vh'
                            boxSizing='border-box'
                            transition="margin-left 0.25s ease"
                            marginLeft={isSidebarOpen ? "250px" : "0px"}

                            border="1px solid #ECECEC"
                            borderRadius="20px"
                            mt={75}
                            mr={2}
                            mb={2}
                            bgGradient="linear(to-b, #EAFFFF 0%, white 30%, white 60%, #EAFFFF 100%)"
                        >
                            <Flex h="calc(100vh - 90px)"
                                px={2}
                                py={2}
                                overflow="hidden"
                            >
                                {/* LEFT PANE */}
                                {isChatLoading ? (
                                    <Flex
                                        flex={isRightPaneOpen ? "0 0 auto" : "1"}
                                        w={isRightPaneOpen ? `${leftWidth}px` : "100%"}
                                        h="100%"
                                        align="center"
                                        justify="center"
                                    >
                                        <Flex align="center" gap={3} direction="row" bg="white" p={6} borderRadius="16px" boxShadow="sm">
                                            <Spinner size="lg" color="#CF008A" />
                                            <Text fontSize="md" color="#4A5568">Loading chat…</Text>
                                        </Flex>
                                    </Flex>
                                ) : (
                                    <Box
                                        flex={isRightPaneOpen ? "0 0 auto" : "1"}
                                        w={isRightPaneOpen ? `${leftWidth}px` : "100%"}
                                        h="100%"
                                        overflow="hidden"
                                    >
                                        <LeftPane
                                            key={chatId || 'draft'}
                                            chatId={chatId}
                                            turns={turns}
                                        activeSuggestionTurnId={activeSuggestionTurnId}
                                        onSendMessage={handleSend}
                                        isLoading={isSending}
                                        messagesContainerRef={messagesContainerRef}
                                        onScrollMessages={onScrollMessages}
                                        isRightPaneOpen={isRightPaneOpen}
                                        typingTurnId={typingTurnId}
                                        typingSuggestionId={typingSuggestionId}
                                        onToggleSuggestion={toggleSuggestion}
                                        reports={reports}
                                        activeReportId={activeReportId}
                                        onToggleReport={toggleReport}
                                        typingReportId={typingReportId}
                                        isLoadingReport={isLoadingReport}
                                        onSubmitFeedback={saveFeedback}
                                        hasMoreTurns={hasMoreTurns}
                                        isLoadingOlderTurns={isLoadingOlderTurns}
                                        onLoadOlderTurns={loadOlderTurns}
                                        feedbackState={feedbackState}
                                        setFeedbackState={setFeedbackState}
                                    />
                                </Box>
                                )}

                                {/* DIVIDER */}
                                {isRightPaneOpen && (
                                    <Box
                                        w="8px"
                                        bg="transparent"
                                        cursor="col-resize"
                                        _hover={{ bg: "transparent" }}
                                        onMouseDown={handleSeparatorMouseDown}
                                    />
                                )}

                                {/* RIGHT PANE */}
                                {isRightPaneOpen && (
                                    <Box flex="1" h="100%" overflow="hidden">
                                        <RightPane
                                            activeTurn={activeTurn}
                                            onClose={closeSuggestionPane}
                                            onScroll={handleRightPaneScroll}
                                            typingSuggestionId={typingSuggestionId}
                                            feedbackState={feedbackState}
                                            setFeedbackState={setFeedbackState}
                                            rightPaneRef={rightPaneRef}
                                            content={rightPaneContent}
                                            typingIndicatorVisible={isTypingInRightPane}
                                            extractedSuggestions={extractedSuggestions[activeSuggestionTurnId]}
                                            selectedSuggestions={selectedSuggestions[activeSuggestionTurnId]}
                                            onConfirmSelection={confirmSuggestionSelection}
                                            onGenerateReport={handleGenerateReport}
                                            turnId={activeSuggestionTurnId}
                                            typingReportId={typingReportId}
                                            isLoadingSuggestions={isLoadingSuggestions}
                                            isLoadingReport={isLoadingReport}
                                            onSubmitFeedback={saveFeedback}
                                            reportLoadingPhase={reportLoadingPhase}
                                            onEnsureExtractedSuggestions={onEnsureExtractedSuggestions}
                                            suggestionExtractionStatus={suggestionExtractionStatus[activeSuggestionTurnId]}
                                        />
                                    </Box>
                                )}
                            </Flex>
                        </Box>
                    )}
                </>

                {/* Bounce animation for typing indicators */}
                <style>
                    {`
                    @keyframes bounce {
                        0%, 100% {
                        transform: translateY(0);
                        }
                        50% {
                        transform: translateY(-5px);
                        }
                    }
                    `}
                </style>
            </Box>

            <OnboardingDialog
                isOpen={showOnboarding}
                user={user}
                onComplete={handleOnboardingComplete}
            />
            <AlertDialog
                isOpen={isNewChatConfirmOpen}
                leastDestructiveRef={cancelNewChatRef}
                onClose={onNewChatConfirmClose}
                isCentered
            >
                <AlertDialogOverlay backdropFilter="blur(4px)" />
                <AlertDialogContent borderRadius="16px" p={2}>
                    <AlertDialogHeader fontSize="18px" fontWeight="bold" color="#1A1A1A">
                        Response in progress
                    </AlertDialogHeader>
                    <AlertDialogBody fontSize="14px" color="#6D6D6D">
                        The current response hasn't finished yet and may not be saved.
                        Are you sure you want to start a new chat?
                    </AlertDialogBody>
                    <AlertDialogFooter gap="8px">
                        <Button
                            ref={cancelNewChatRef}
                            variant="ghost"
                            color="#6D6D6D"
                            borderRadius="10px"
                            _hover={{ bg: '#F5F5F5' }}
                            onClick={onNewChatConfirmClose}
                        >
                            Wait
                        </Button>
                        <Button
                            bg="#CF008A"
                            color="white"
                            borderRadius="10px"
                            _hover={{ bg: '#D933A1' }}
                            onClick={() => { onNewChatConfirmClose(); resetChat(); navigate('/'); }}
                        >
                            Start new chat
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Box>
    );
}